<template>
  <div class="shipping-quote-widget">
    <div class="sq-header" role="button" @click="expanded = !expanded">
      <div class="sq-header-text">
        <span class="sq-header-title">
          <template v-if="selectedRate && effectiveFreeShipping">Envío: GRATIS</template>
          <template v-else-if="selectedRate">Envío: {{ fmtPrice(selectedRate.price) }}</template>
          <template v-else>Cotizá tu envío</template>
        </span>
        <span class="sq-header-sub">
          <template v-if="selectedRate">
            {{ selectedRate.productName }} — {{ selectedRate.deliveryTimeMin }}–{{ selectedRate.deliveryTimeMax }} días hábiles
            <span class="sq-header-delivery-badge" :class="isDoor(selectedRate) ? 'sq-hdb--door' : 'sq-hdb--branch'">
              <i :class="isDoor(selectedRate) ? 'fas fa-home' : 'fas fa-building'"></i>
              {{ isDoor(selectedRate) ? 'A domicilio' : 'En sucursal' }}
            </span>
          </template>
          <template v-else-if="externalPostalCode">Se calcula con tu código postal</template>
          <template v-else>Ingresá tu CP y conocé el costo</template>
        </span>
      </div>
      <i class="fas sq-chevron" :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
    </div>

    <transition name="sq-slide">
      <div v-show="expanded" class="sq-body">
        <div v-if="!externalPostalCode" class="sq-input-row">
          <div class="sq-input-wrapper">
            <i class="fas fa-map-marker-alt sq-input-icon"></i>
            <input
              v-model="postalCode"
              type="text"
              class="form-control sq-input"
              placeholder="Ej: 5000"
              maxlength="8"
              @keyup.enter="fetchRates"
            />
          </div>
          <button type="button" class="btn sq-btn" :disabled="loading || !canQuote" @click="fetchRates">
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <template v-else><i class="fas fa-search me-1"></i>Cotizar</template>
          </button>
        </div>

        <div v-if="externalPostalCode && loading" class="sq-loading-ext">
          <i class="fas fa-spinner fa-spin me-2"></i>Calculando envío para CP {{ postalCode }}...
        </div>

        <div v-if="rates.length" class="sq-rates">
          <div
            v-for="(rate, idx) in rates"
            :key="idx"
            class="sq-rate"
            :class="{ 'sq-rate--selected': selectedRate === rate }"
            @click="selectRate(rate)"
          >
            <span class="sq-radio" :class="{ 'sq-radio--on': selectedRate === rate }"></span>
            <div class="sq-rate-left">
              <strong class="sq-rate-name">{{ rate.productName }}</strong>
              <small class="sq-rate-time">
                <i class="fas fa-clock me-1"></i>{{ rate.deliveryTimeMin }}–{{ rate.deliveryTimeMax }} días hábiles
              </small>
              <span class="badge sq-rate-badge" :class="isDoor(rate) ? 'bg-delivery' : 'bg-pickup'">
                <i :class="isDoor(rate) ? 'fas fa-home' : 'fas fa-building'" class="me-1"></i>
                {{ isDoor(rate) ? 'A domicilio' : 'En sucursal' }}
              </span>
            </div>
            <span class="sq-rate-price">
              <template v-if="effectiveFreeShipping">
                <span class="sq-price-strike">{{ fmtPrice(rate.price) }}</span>
                <span class="sq-price-free">GRATIS</span>
              </template>
              <template v-else>{{ fmtPrice(rate.price) }}</template>
            </span>
          </div>
        </div>

        <div v-if="errorMsg" class="sq-error">
          <i class="fas fa-exclamation-triangle me-2"></i>{{ errorMsg }}
        </div>

        <div v-if="rates.length" class="sq-dispatch-notice">
          <i class="fas fa-info-circle me-1"></i>
          Los plazos cuentan desde el despacho del paquete.
        </div>

        <div v-if="rates.length && (!selectedRate || isDoor(selectedRate))" class="sq-branches-link">
          <button type="button" class="branches-btn" @click="openBranchModal(false)">
            <i class="fas fa-map-marked-alt me-2"></i>Ver sucursales disponibles
          </button>
        </div>

        <div v-if="selectedRate && !isDoor(selectedRate)" class="sq-branch-required alert border-0 mt-3 mb-0">
          <i class="fas fa-building me-2 text-warning"></i>
          <div class="sq-branch-required-body">
            <strong class="d-block">Retiro en sucursal</strong>
            <span v-if="!selectedBranch" class="text-warning small">Seleccioná una sucursal para continuar.</span>
            <span v-else class="text-success small">
              <i class="fas fa-check-circle me-1"></i>{{ selectedBranch.name }}
            </span>
            <button type="button" class="btn btn-sm btn-outline-warning mt-2" @click="openBranchModal(true)">
              <i class="fas fa-map-marker-alt me-1"></i>{{ selectedBranch ? 'Cambiar sucursal' : 'Elegir sucursal' }}
            </button>
          </div>
        </div>

        <div v-if="!attempted && !errorMsg && !rates.length && !loading" class="sq-hint">
          <i class="fas fa-map-pin me-1"></i>
          Envíos con Correo Argentino a todo el país.
        </div>
      </div>
    </transition>

    <BranchLocatorModal
      v-model="showBranchModal"
      :initial-postal-code="postalCode"
      :selection-mode="branchModalSelectionMode"
      @branch-selected="onBranchSelected"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineProps, defineEmits } from 'vue'
import { getShippingPublicConfig, createShippingQuote, selectShippingQuoteRate } from '@/services/shipping'
import BranchLocatorModal from '@/components/shipping/BranchLocatorModal.vue'

const props = defineProps({
  quoteSource: { type: String, default: 'wholesale' },
  quoteItems: { type: Array, default: () => [] },
  declaredValue: { type: Number, default: 0 },
  freeShippingInfo: { type: Object, default: null },
  autoSelect: { type: Boolean, default: true },
  startExpanded: { type: Boolean, default: true },
  externalPostalCode: { type: String, default: '' },
})

const emit = defineEmits(['rate-selected'])

const expanded = ref(props.startExpanded)
const postalCode = ref(props.externalPostalCode || '')
const loading = ref(false)
const errorMsg = ref('')
const attempted = ref(false)
const rates = ref([])
const selectedRate = ref(null)
const quoteId = ref(null)
const freeShippingTesting = ref(false)
const showBranchModal = ref(false)
const branchModalSelectionMode = ref(false)
const selectedBranch = ref(null)
let fetchDebounceTimer = null

const effectiveFreeShipping = computed(
  () => props.freeShippingInfo?.free_shipping || freeShippingTesting.value
)

const canQuote = computed(() => {
  const cp = postalCode.value.trim()
  return cp.length >= 4 && props.quoteItems.length > 0
})

const isDoor = (rate) => {
  const t = rate?.deliveredType || rate?.deliveryType || ''
  return t === 'D' || t === ''
}

const fmtPrice = (v) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(v || 0)

const emitSelection = () => {
  const rate = selectedRate.value
  if (!rate) {
    emit('rate-selected', null)
    return
  }
  if (!isDoor(rate) && !selectedBranch.value) {
    emit('rate-selected', { incomplete: true, needsBranch: true })
    return
  }
  emit('rate-selected', {
    rate,
    quoteId: quoteId.value,
    postalCode: postalCode.value.trim(),
    selectedBranch: selectedBranch.value,
    freeShipping: effectiveFreeShipping.value,
  })
}

const fetchRates = async () => {
  const cp = postalCode.value.trim()
  if (!cp || cp.length < 4 || !props.quoteItems.length) return

  loading.value = true
  errorMsg.value = ''
  rates.value = []
  selectedRate.value = null
  selectedBranch.value = null
  quoteId.value = null

  try {
    const result = await createShippingQuote({
      source: props.quoteSource,
      postal_code: cp,
      items: props.quoteItems,
    })
    freeShippingTesting.value = !!result.free_shipping_testing
    quoteId.value = result.id
    if (result.rates?.length) {
      rates.value = result.rates
      if (props.autoSelect) selectRate(result.rates[0])
    } else {
      errorMsg.value = 'No hay opciones de envío para ese código postal.'
      emit('rate-selected', null)
    }
  } catch (err) {
    errorMsg.value =
      err.response?.data?.detail ||
      err.response?.data?.error ||
      'No pudimos calcular el envío. Intentá nuevamente.'
    emit('rate-selected', null)
  } finally {
    loading.value = false
    attempted.value = true
  }
}

const selectRate = async (rate) => {
  selectedRate.value = rate
  selectedBranch.value = null
  if (quoteId.value && rate) {
    try {
      await selectShippingQuoteRate(quoteId.value, rate)
    } catch {
      /* ignore */
    }
  }
  emitSelection()
}

const openBranchModal = (forSelection) => {
  branchModalSelectionMode.value = forSelection
  showBranchModal.value = true
}

const onBranchSelected = (branch) => {
  selectedBranch.value = branch
  emitSelection()
}

watch(
  () => props.externalPostalCode,
  (newCP) => {
    if (!newCP) {
      postalCode.value = ''
      rates.value = []
      selectedRate.value = null
      quoteId.value = null
      emit('rate-selected', null)
      return
    }
    postalCode.value = newCP
    if (newCP.length >= 4 && props.quoteItems.length) {
      clearTimeout(fetchDebounceTimer)
      fetchDebounceTimer = setTimeout(() => {
        expanded.value = true
        fetchRates()
      }, 500)
    }
  },
  { immediate: true }
)

watch(
  () => props.quoteItems,
  () => {
    if (attempted.value && postalCode.value.length >= 4) fetchRates()
  },
  { deep: true }
)

onMounted(async () => {
  try {
    await getShippingPublicConfig()
  } catch {
    /* silencioso */
  }
})

defineExpose({ fetchRates, selectedRate, quoteId, postalCode })
</script>

<style scoped>
.shipping-quote-widget {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
}
.sq-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  cursor: pointer;
  background: rgba(255, 236, 0, 0.06);
}
.sq-header-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.sq-header-title {
  font-weight: 700;
  font-size: 0.92rem;
}
.sq-header-sub {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.55);
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}
.sq-header-delivery-badge {
  font-size: 0.65rem;
  padding: 1px 7px;
  border-radius: 20px;
}
.sq-hdb--door {
  background: rgba(255, 236, 0, 0.15);
  color: var(--primary-color, #ffec00);
}
.sq-hdb--branch {
  background: rgba(100, 180, 255, 0.15);
  color: #8ec5ff;
}
.sq-chevron {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.75rem;
}
.sq-body {
  padding: 0.85rem 1rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.sq-input-row {
  display: flex;
  gap: 0.5rem;
}
.sq-input-wrapper {
  flex: 1;
  position: relative;
}
.sq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
}
.sq-input {
  padding-left: 34px;
  background: rgba(0, 0, 0, 0.25) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  color: #fff !important;
}
.sq-btn {
  background: var(--primary-color, #ffec00);
  color: #111;
  border: none;
  font-weight: 600;
  min-width: 100px;
}
.sq-btn:disabled {
  opacity: 0.45;
}
.sq-rates {
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.sq-rate {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 10px;
  border: 2px solid transparent;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
}
.sq-rate--selected {
  border-color: var(--primary-color, #ffec00);
  background: rgba(255, 236, 0, 0.08);
}
.sq-radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #555;
  flex-shrink: 0;
  position: relative;
}
.sq-radio--on {
  border-color: var(--primary-color, #ffec00);
}
.sq-radio--on::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary-color, #ffec00);
}
.sq-rate-left {
  flex: 1;
  min-width: 0;
}
.sq-rate-name {
  font-size: 0.86rem;
  display: block;
}
.sq-rate-time {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.76rem;
}
.sq-rate-badge {
  font-size: 0.68rem;
  margin-top: 3px;
}
.bg-delivery {
  background: rgba(255, 236, 0, 0.15);
  color: var(--primary-color, #ffec00);
}
.bg-pickup {
  background: rgba(100, 180, 255, 0.15);
  color: #8ec5ff;
}
.sq-rate-price {
  font-weight: 700;
  white-space: nowrap;
}
.sq-price-strike {
  text-decoration: line-through;
  opacity: 0.5;
  font-size: 0.8em;
  margin-right: 4px;
}
.sq-price-free {
  color: #5be05b;
}
.sq-error {
  margin-top: 0.75rem;
  padding: 0.6rem;
  border-radius: 8px;
  background: rgba(255, 193, 7, 0.12);
  color: #ffc107;
  font-size: 0.82rem;
}
.sq-hint,
.sq-dispatch-notice,
.sq-loading-ext {
  margin-top: 0.6rem;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
}
.sq-branches-link {
  margin-top: 0.65rem;
  text-align: center;
}
.branches-btn {
  border: 1px solid rgba(255, 236, 0, 0.25);
  background: rgba(255, 236, 0, 0.08);
  color: var(--primary-color, #ffec00);
  border-radius: 8px;
  padding: 0.4rem 0.75rem;
  font-size: 0.78rem;
  cursor: pointer;
}
.sq-branch-required {
  display: flex;
  gap: 0.5rem;
  background: rgba(255, 193, 7, 0.08);
  padding: 0.75rem;
  border-radius: 10px;
}
.sq-branch-required > i {
  flex-shrink: 0;
  margin-top: 0.15rem;
}
.sq-branch-required-body {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
}
.sq-branch-required-body strong,
.sq-branch-required-body span {
  line-height: 1.35;
}
.sq-slide-enter-active,
.sq-slide-leave-active {
  transition: max-height 0.3s ease, opacity 0.2s ease;
  overflow: hidden;
}
.sq-slide-enter-from,
.sq-slide-leave-to {
  max-height: 0;
  opacity: 0;
}
.sq-slide-enter-to,
.sq-slide-leave-from {
  max-height: 800px;
  opacity: 1;
}
</style>
